import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import ImprovedSidebar from './ImprovedSidebar';
import '../styles/ImprovedSidebar.css';
import Header from './Header';
import Footer from './Footer';
import DemoTopBanner from './demo/DemoTopBanner';
import { useAppContext } from '../contexts/AppContext';

const AppLayout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      return width >= 768 && width < 1024;
    }
    return false;
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  const [isTablet, setIsTablet] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      return width >= 768 && width < 1024;
    }
    return false;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const { requiresSelection, isLoading } = useAppContext();
  const location = useLocation();

  useEffect(() => {
    document.documentElement.setAttribute('data-density', 'compact');
  }, []);

  const isAnasayfa = location.pathname === '/anasayfa';

  if (!isLoading && !isAnasayfa && requiresSelection) {
    return <Navigate to="/anasayfa" replace />;
  }

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, isMobileMenuOpen]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const getSidebarWidth = () => {
    if (isMobile) return '0px';
    if (sidebarCollapsed) return 'var(--sidebar-width-collapsed, 78px)';
    return 'var(--sidebar-width-expanded, 356px)';
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {isMobile && isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          style={{ zIndex: 29990 }}
          onClick={(e) => {
            e.preventDefault();
            closeMobileMenu();
          }}
        />
      )}

      <ImprovedSidebar
        isCollapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={closeMobileMenu}
        isMobile={isMobile}
      />

      <div
        className="flex-1 flex flex-col transition-all duration-300"
        style={{
          marginLeft: isMobile ? '0px' : getSidebarWidth(),
          width: isMobile ? '100%' : `calc(100% - ${getSidebarWidth()})`,
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          overflowX: 'hidden'
        }}
      >
        <Header onMobileMenuToggle={toggleMobileMenu} isMobile={isMobile} />
        <DemoTopBanner />

        <main className="flex-grow" style={{ padding: 'var(--layout-content-padding)' }}>
          <Outlet />
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default AppLayout;
